import { useContext, useState } from 'react';
import * as R from 'ramda';

import WorkTemplateTree from '@atom/components/common/workTemplateTree/WorkTemplateTree';
import { Button, Modal } from '@atom/mui';
import { WorkOrderTemplateFolder } from '@atom/types/workTemplate';

import WorkOrdersFiltersContext from './WorkOrdersFiltersContext';

import classes from './filters.module.css';

const styles = {
  button: {
    minWidth: 0,
    padding: 0,
  },
  modal: {
    height: '50vh',
    padding: '2rem',
  },
};

const TemplateFolderFilter = () => {
  const { schemaId, areFiltersDisabled, filterForm, updateFilterForm } =
    useContext(WorkOrdersFiltersContext);

  const { workTemplateFolders: workTemplateFolderFilter } = filterForm;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFolders, setSelectedFolders] = useState<
    WorkOrderTemplateFolder[]
  >(workTemplateFolderFilter);

  const selectedFoldersIds = new Set(selectedFolders.map(folder => folder.id));

  const handleToggleFolder = (toggleFolder: WorkOrderTemplateFolder) => {
    const isSelected = selectedFoldersIds.has(toggleFolder.id);
    const updatedSelected = isSelected
      ? selectedFolders.filter(folder => folder.id !== toggleFolder.id)
      : [...selectedFolders, toggleFolder];
    setSelectedFolders(updatedSelected);
  };

  const onOpenModal = () => {
    setIsModalOpen(true);
    setSelectedFolders(workTemplateFolderFilter);
  };

  const onCancelModal = () => {
    setSelectedFolders(workTemplateFolderFilter);
    setIsModalOpen(false);
  };

  const onConfirmModal = () => {
    updateFilterForm('workTemplateFolders', selectedFolders);
    setIsModalOpen(false);
  };

  const onClearModal = () => {
    setSelectedFolders([]);
  };

  const onClearFilter = () => {
    updateFilterForm('workTemplateFolders', []);
  };

  return (
    <>
      <div className={classes.filterContainer}>
        <div className={classes.filterLabel}>Work Template Folder</div>
        <div>
          {workTemplateFolderFilter.map(folder => folder.name).join(', ')}
        </div>
        <div className={classes.filterButtonPair}>
          <Button
            size="small"
            onClick={onOpenModal}
            style={styles.button}
            disabled={areFiltersDisabled}
            data-cy="workOrdersFilterTemplateFolderSelectButton"
          >
            select
          </Button>
          {!R.isEmpty(workTemplateFolderFilter) && (
            <Button
              size="small"
              onClick={onClearFilter}
              style={styles.button}
              disabled={areFiltersDisabled}
              data-cy="workOrdersFilterTemplateFolderClearButton"
            >
              clear
            </Button>
          )}
        </div>
      </div>
      <Modal
        open={isModalOpen}
        title="Select Work Template Folder"
        onCancel={onCancelModal}
        onConfirm={onConfirmModal}
        onClear={onClearModal}
        width="lg"
        contentStyle={styles.modal}
        data-cy="workOrdersFilterTemplateFolderModal"
        CancelButtonProps={{
          'data-cy': 'workOrdersFilterTemplateFolderModalCancelButton',
        }}
        ConfirmButtonProps={{
          'data-cy': 'workOrdersFilterTemplateFolderModalConfirmButton',
        }}
        ClearButtonProps={{
          'data-cy': 'workOrdersFilterTemplateFolderModalClearButton',
        }}
      >
        <WorkTemplateTree
          autoSelectChildFolders
          hideTemplates
          includeSearch={false}
          schemaId={schemaId}
          hasSchema={false}
          onFolderToggle={handleToggleFolder}
          getFolderSelected={folder => selectedFoldersIds.has(folder.id)}
        />
      </Modal>
    </>
  );
};

export default TemplateFolderFilter;
